import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Play, BookOpen, MessageCircle, Heart, Award, PenLine, Filter, Users, Zap, TrendingUp, Sparkles, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import PathwaysLogo from '../components/PathwaysLogo';
import XPProgressBar from '../components/XPProgressBar';
import LevelBadge from '../components/LevelBadge';
import GenerateScenarioModal from '../components/GenerateScenarioModal';
import CategoryExplorer from '../components/CategoryExplorer';
import LanguageSelector from '../components/LanguageSelector';
import NotificationBell from '../components/NotificationBell';
import SkipLink from '../components/accessibility/SkipLink';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ userId }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [scenarios, setScenarios] = useState([]);
  const [progress, setProgress] = useState([]);
  const [profile, setProfile] = useState(null);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [displayCount, setDisplayCount] = useState(9); // Show 9 initially
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const SCENARIOS_PER_PAGE = 9;

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch scenarios when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      fetchData();
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Reset display count when filter changes
  useEffect(() => {
    setDisplayCount(9);
  }, [categoryFilter]);

  const fetchData = async () => {
    try {
      const currentLang = localStorage.getItem('preferred_language') || 'en';
      // Call profile first to ensure user exists (auto-creates if needed)
      const profileRes = await axios.get(`${API}/profile/${userId}`);
      setProfile(profileRes.data);
      
      // Then fetch other data in parallel
      const [scenariosRes, progressRes, challengeRes] = await Promise.all([
        axios.get(`${API}/scenarios/filtered/${userId}?lang=${currentLang}`), // Use filtered endpoint with language
        axios.get(`${API}/progress/${userId}`),
        axios.get(`${API}/daily-challenge`)
      ]);
      setScenarios(scenariosRes.data);
      setProgress(progressRes.data);
      setDailyChallenge(challengeRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressForScenario = (scenarioId) => {
    return progress.find(p => p.scenario_id === scenarioId);
  };

  const filteredScenarios = categoryFilter === 'all' 
    ? scenarios 
    : scenarios.filter(s => s.category === categoryFilter);
  
  const displayedScenarios = filteredScenarios.slice(0, displayCount);
  const hasMore = displayCount < filteredScenarios.length;

  const loadMore = () => {
    setDisplayCount(prev => prev + SCENARIOS_PER_PAGE);
  };

  const handleScenarioGenerated = (newScenario) => {
    // Add new scenario to the list
    setScenarios(prev => [newScenario, ...prev]);
    setDisplayCount(9); // Reset to show new scenario
  };

  const categories = ['all', ...new Set(scenarios.map(s => s.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  // Detect reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionProps = prefersReducedMotion ? {} : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

  return (
    <>
      <SkipLink />
      <div className="app-container min-h-screen p-4 md:p-6 lg:p-8 pb-24 md:pb-28 bg-[#F4F7F6] overflow-x-hidden">
        <motion.div {...motionProps}>
          <div className="max-w-7xl mx-auto">
            {/* Header with Pathways Logo & SSDP Attribution */}
            <div className="mb-6 sm:mb-8 flex items-start justify-between">
              <div>
                <PathwaysLogo size="md" animated={!prefersReducedMotion} showText={true} />
                <p className="text-sm sm:text-base md:text-lg text-ssdp-navy-light mt-1 sm:mt-2 font-medium font-body">
                  Navigate life&apos;s complex decisions with confidence
                </p>
              </div>
              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                <LanguageSelector />
                <NotificationBell userId={userId} />
                <div className="hidden sm:flex flex-col items-end gap-2">
                  <img 
                    src="/ssdp-logo.jpg" 
                    alt="SSDP International logo" 
                    className="w-12 h-12 md:w-16 md:h-16 rounded-full border-3 border-ssdp-navy shadow-[4px_4px_0px_0px_rgba(30,58,89,1)]"
                  />
                  <span className="text-xs text-ssdp-navy-light/60 font-body whitespace-nowrap">
                    by SSDP International
                  </span>
                </div>
              </div>
            </div>

            <main id="main-content" tabIndex="-1" className="focus:outline-none">
              {profile && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onClick={() => navigate('/profile')}
                    className="lg:col-span-2 bg-ssdp-teal border-4 border-ssdp-navy rounded-3xl p-6 shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] cursor-pointer hover:shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                    data-testid="profile-widget"
                    aria-label={`View profile: ${profile.username}, Level ${profile.level}, ${profile.xp} XP`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <LevelBadge level={profile.level} size="md" />
                        <div>
                          <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight font-heading">
                            {profile.username}
                          </h3>
                          <p className="text-white/90 text-sm font-medium font-body">
                            Level {profile.level} • {profile.xp} XP
                          </p>
                        </div>
                      </div>
                      <TrendingUp className="w-8 h-8 text-white" strokeWidth={3} aria-hidden="true" />
                    </div>
                    <XPProgressBar currentXP={profile.xp} level={profile.level} showLabel={false} />
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      <div className="bg-white border-3 border-ssdp-navy rounded-xl p-3 text-center shadow-[3px_3px_0px_0px_rgba(30,58,89,1)]">
                        <div className="text-2xl font-black text-ssdp-navy font-heading" aria-label={`${profile.current_streak} day streak`}>
                          {profile.current_streak}
                        </div>
                        <div className="text-xs text-ssdp-navy-light font-body">
                          {t('dashboard.dayStreak')}
                        </div>
                      </div>
                      <div className="bg-white border-3 border-ssdp-navy rounded-xl p-3 text-center shadow-[3px_3px_0px_0px_rgba(30,58,89,1)]">
                        <div className="text-2xl font-black text-ssdp-navy font-heading" aria-label={`${profile.completed_scenarios?.length || 0} completed scenarios`}>
                          {profile.completed_scenarios?.length || 0}
                        </div>
                        <div className="text-xs text-ssdp-navy-light font-body">
                          {t('dashboard.completedScenarios')}
                        </div>
                      </div>
                      <div className="bg-white border-3 border-ssdp-navy rounded-xl p-3 text-center shadow-[3px_3px_0px_0px_rgba(30,58,89,1)]">
                        <div className="text-2xl font-black text-ssdp-navy font-heading" aria-label={`${profile.achievements?.length || 0} badges earned`}>
                          {profile.achievements?.length || 0}
                        </div>
                        <div className="text-xs text-ssdp-navy-light font-body">
                          Badges
                        </div>
                      </div>
                    </div>
                  </motion.div>

              {dailyChallenge && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  onClick={() => navigate(`/story/${dailyChallenge.scenario_id}`)}
                  className="bg-ssdp-purple border-4 border-ssdp-navy rounded-3xl p-6 shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] cursor-pointer hover:shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  data-testid="daily-challenge-card"
                  aria-label={`Daily Challenge: ${dailyChallenge.title?.replace('Daily Challenge: ', '')} for ${dailyChallenge.xp_bonus} bonus XP`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-8 h-8 text-white" fill="white" strokeWidth={0} aria-hidden="true" />
                    <h3 className="text-xl font-black text-white uppercase tracking-tight font-heading">
                      Daily Challenge
                    </h3>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2 font-body">
                    {dailyChallenge.title?.replace('Daily Challenge: ', '')}
                  </h4>
                  <p className="text-white/90 text-sm mb-4 font-body">
                    {dailyChallenge.description}
                  </p>
                  <div className="bg-ssdp-navy text-white px-3 py-1 rounded-full text-sm font-black inline-block font-heading shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)]">
                    +{dailyChallenge.xp_bonus} BONUS XP
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Generate Scenario Button (Level 3+) */}
          {profile && profile.level >= 3 && (
            <motion.section
              {...(prefersReducedMotion ? {} : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.3 } })}
              className="mb-6"
            >
              <Button
                onClick={() => setShowGenerateModal(true)}
                className="w-full bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white font-black px-8 py-6 rounded-3xl border-4 border-ssdp-navy shadow-[8px_8px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all font-heading min-h-[44px] focus:outline-none focus:ring-4 focus:ring-ssdp-teal focus:ring-offset-4"
                data-testid="generate-scenario-button"
                aria-label="Generate new AI-powered scenario"
              >
                <Sparkles className="w-6 h-6 mr-3" aria-hidden="true" />
                <span className="text-2xl">Generate New Scenario</span>
                <span className="ml-3 bg-ssdp-navy text-ssdp-purple px-3 py-1 rounded-full text-sm shadow-[2px_2px_0px_0px_rgba(255,255,255,0.2)]">
                  ✨ AI Powered
                </span>
              </Button>
            </motion.section>
          )}

          {/* Category Explorer */}
          <CategoryExplorer 
            scenarios={scenarios}
            completedScenarios={profile?.completed_scenarios || []}
            onCategoryClick={(category) => setCategoryFilter(category)}
          />

          <section className="mb-8 flex items-center gap-4" aria-label="Filter scenarios">
            <Filter className="w-5 h-5 text-ssdp-navy" strokeWidth={3} aria-hidden="true" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger 
                className="w-64 bg-white border-3 border-ssdp-navy rounded-2xl shadow-[3px_3px_0px_0px_rgba(30,58,89,1)] min-h-[44px] focus:outline-none focus:ring-4 focus:ring-ssdp-teal focus:ring-offset-2" 
                data-testid="category-filter"
                aria-label="Filter scenarios by category"
              >
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent className="bg-white border-3 border-ssdp-navy rounded-2xl">
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat} className="rounded-xl font-body">
                    {cat === 'all' ? 'All Scenarios' : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-ssdp-navy-light font-medium font-body" aria-live="polite">
              {filteredScenarios.length} {filteredScenarios.length === 1 ? 'scenario' : 'scenarios'}
            </span>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            {displayedScenarios.map((scenario, index) => {
              const scenarioProgress = getProgressForScenario(scenario.id);
              const isCompleted = scenarioProgress?.completed;
              
              return (
                <motion.div
                  key={scenario.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-3xl border-4 border-ssdp-navy overflow-hidden cursor-pointer transition-all shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] hover:shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:translate-x-[2px] hover:translate-y-[2px]"
                  onClick={() => navigate(`/story/${scenario.id}`)}
                  data-testid={`scenario-card-${scenario.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={scenario.thumbnail}
                      alt={scenario.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ssdp-navy/60 to-transparent" />
                    {isCompleted && (
                      <div className="absolute top-4 right-4 bg-ssdp-teal text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1 border-3 border-ssdp-navy shadow-[3px_3px_0px_0px_rgba(30,58,89,1)] font-heading">
                        <Award className="w-4 h-4" />
                        Completed
                      </div>
                    )}
                    {scenario.is_micro && (
                      <div className="absolute top-4 left-4 bg-[#CCFF00] text-ssdp-navy px-3 py-1 rounded-full text-xs font-black flex items-center gap-1 border-3 border-ssdp-navy shadow-[2px_2px_0px_0px_rgba(30,58,89,1)] font-heading animate-pulse">
                        <Zap className="w-3 h-3" />
                        QUICK
                      </div>
                    )}
                    {scenario.is_generated && !scenario.is_micro && (
                      <div className="absolute top-4 left-4 bg-ssdp-purple text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 border-2 border-ssdp-navy shadow-[2px_2px_0px_0px_rgba(30,58,89,1)] font-heading">
                        <Sparkles className="w-3 h-3" />
                        AI Generated
                      </div>
                    )}
                    {scenario.favorited_by?.includes(userId) && (
                      <div className="absolute top-4 right-4 bg-ssdp-purple text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 border-2 border-ssdp-navy shadow-[2px_2px_0px_0px_rgba(30,58,89,1)] font-heading">
                        <Star className="w-3 h-3" fill="white" />
                        Favorite
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-white/90 text-sm font-medium font-body">{scenario.category}</span>
                      <h3 className="text-white text-2xl font-bold font-heading break-words">{scenario.title}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-ssdp-navy-light mb-4 font-body break-words overflow-wrap-anywhere">
                      {scenario.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ssdp-navy-light font-medium font-body">{scenario.duration}</span>
                      <Button
                        className="bg-ssdp-teal text-white font-bold px-6 py-2 rounded-full hover:scale-105 transition-transform border-3 border-ssdp-navy shadow-[3px_3px_0px_0px_rgba(30,58,89,1)] font-heading"
                        data-testid={`start-story-${scenario.id}`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {scenarioProgress && !isCompleted ? 'Continue' : 'Start'}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mb-8">
              <Button
                onClick={loadMore}
                className="bg-ssdp-teal text-white font-bold px-8 py-4 rounded-2xl border-4 border-ssdp-navy shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] hover:shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all font-heading min-h-[44px] focus:outline-none focus:ring-4 focus:ring-ssdp-teal focus:ring-offset-4"
                data-testid="load-more-button"
                aria-label={`Load ${filteredScenarios.length - displayCount} more scenarios`}
              >
                <TrendingUp className="w-5 h-5 mr-2" aria-hidden="true" />
                Load More Scenarios ({filteredScenarios.length - displayCount} remaining)
              </Button>
            </div>
          )}

          <nav className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6" aria-label="Quick navigation">
            <motion.button
              {...(prefersReducedMotion ? {} : { whileHover: { scale: 1.02 } })}
              className="bg-[#FF0080] text-white rounded-3xl p-6 cursor-pointer border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-left min-h-[44px] focus:outline-none focus:ring-4 focus:ring-[#FF0080] focus:ring-offset-4"
              onClick={() => navigate('/resources')}
              data-testid="resources-card"
              aria-label="Resources - Get help when you need it"
            >
              <BookOpen className="w-12 h-12 mb-4" strokeWidth={3} aria-hidden="true" />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Resources</h3>
              <p className="text-white/90" style={{ fontFamily: 'Figtree, sans-serif' }}>Get help when you need it</p>
            </motion.button>

            <motion.button
              {...(prefersReducedMotion ? {} : { whileHover: { scale: 1.02 } })}
              className="bg-white border-4 border-black rounded-3xl p-6 cursor-pointer shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-left min-h-[44px] focus:outline-none focus:ring-4 focus:ring-[#00E5FF] focus:ring-offset-4"
              onClick={() => navigate('/real-voices')}
              data-testid="real-voices-card"
              aria-label="Real Voices - Share and read stories"
            >
              <Users className="w-12 h-12 mb-4 text-[#00E5FF]" strokeWidth={3} aria-hidden="true" />
              <h3 className="text-2xl font-bold mb-2 text-black" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Real Voices</h3>
              <p className="text-black/70" style={{ fontFamily: 'Figtree, sans-serif' }}>Share & read stories</p>
            </motion.button>

            <motion.button
              {...(prefersReducedMotion ? {} : { whileHover: { scale: 1.02 } })}
              className="bg-white border-4 border-black rounded-3xl p-6 cursor-pointer shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-left min-h-[44px] focus:outline-none focus:ring-4 focus:ring-[#FFD800] focus:ring-offset-4"
              onClick={() => navigate('/forum')}
              data-testid="forum-card"
              aria-label="Forum - Anonymous discussions"
            >
              <MessageCircle className="w-12 h-12 mb-4 text-[#FFD800]" strokeWidth={3} aria-hidden="true" />
              <h3 className="text-2xl font-bold mb-2 text-black" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Forum</h3>
              <p className="text-black/70" style={{ fontFamily: 'Figtree, sans-serif' }}>Anonymous discussions</p>
            </motion.button>

            <motion.button
              {...(prefersReducedMotion ? {} : { whileHover: { scale: 1.02 } })}
              className="bg-white border-4 border-black rounded-3xl p-6 cursor-pointer shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-left min-h-[44px] focus:outline-none focus:ring-4 focus:ring-[#FF3B30] focus:ring-offset-4"
              onClick={() => navigate('/profile')}
              data-testid="profile-card"
              aria-label="Progress - Track your journey"
            >
              <Award className="w-12 h-12 mb-4 text-[#FF3B30]" strokeWidth={3} aria-hidden="true" />
              <h3 className="text-2xl font-bold mb-2 text-black" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Progress</h3>
              <p className="text-black/70" style={{ fontFamily: 'Figtree, sans-serif' }}>Track your journey</p>
            </motion.button>
          </nav>
        </main>
        </div>
      </motion.div>

      {/* Generate Scenario Modal */}
      <GenerateScenarioModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        userId={userId}
        onScenarioGenerated={handleScenarioGenerated}
      />
    </div>
    </>
  );
};

export default Dashboard;
